import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { APIResultListManageCompanySlotResponse, APIResultRegisterCompanyResponse, CompanySlotControllerService, CompanySlotUpdateRequest, DietaryResponse, EventInfoWithConsentResponse, ManageCompanySlotResponse, ParticipantControllerService, ParticipantRequest, RegisterCompanyControllerService, RegisterCompanyResponse, ScheduleCompanyResponse } from '@ent-regis/entregis-ts-angular';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { of, Subscription } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { msg } from '../constants/msg';
import { AlertService } from '../shared/services/alert.service';
import { AuthService } from '../shared/services/auth.service';
import { DietaryService } from '../shared/services/dietary.service';
import { EventService } from '../shared/services/event.service';
import { buildParticipantForm } from './forms/participant-form.factory';
import { InfoComponent } from './info/info.component';
import { TimetableComponent } from './timetable/timetable.component';
import { LoginLayoutComponent } from '../layouts/login-layout/login-layout/login-layout.component';

@Component({
  selector: 'app-company',
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.scss']
})
export class CompanyComponent implements OnInit, OnDestroy {

  @ViewChild('tabSet', { static: false }) tabSet;
  @ViewChild(TimetableComponent, { static: false }) timetableComponent: TimetableComponent;
  @ViewChild(InfoComponent, { static: false }) infoComponent: InfoComponent;
  slots: ManageCompanySlotResponse[] = [];
  private subscription: Subscription;

  public eventInfo: EventInfoWithConsentResponse
  public registerCompanyInfo: RegisterCompanyResponse;
  public eventAlias: string;
  public registerCompanyId: number;
  public personName: string;
  startDate: Date;
  endDate: Date;
  eventTypeEnum = EventInfoWithConsentResponse.EventTypeEnum;
  leave = true;

  faUser = faUser;

  form!: FormGroup;
  public dietaries: DietaryResponse[] = [];
  public noneDietaryId: number;
  public scheduleCompanyResponse: ScheduleCompanyResponse;
  public isCloseRegister: boolean = false;

  activeTab: 'participants' | 'availableTime' | 'presentation' | 'meetingSchedule' = 'participants';
  private lastSavedParticipants: any[] = [];
  private lastSavedSlots: any[] = [];
  private removeExistingPresentation: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private registerCompanyService: RegisterCompanyControllerService,
    private eventService: EventService,
    private authService: AuthService,
    private participantControllerService: ParticipantControllerService,
    private dietaryService: DietaryService,
    private alert: AlertService,
    private companySlotControllerService: CompanySlotControllerService,
    private loginLayout: LoginLayoutComponent,
  ) { }


  getRegisterCompanyInfo() {
    this.registerCompanyService.getRegisterCompanyByIdUsingGET(this.registerCompanyId)
      .subscribe((res: APIResultRegisterCompanyResponse) => {
        this.registerCompanyInfo = res.data;
        this.isCloseRegister = res.data && res.data.isCloseRegister;    
      });
  }

  ngOnInit(): void {
    this.subscription = new Subscription();

    this.registerCompanyId = Number(this.route.snapshot.paramMap.get('registerCompanyId'));
    this.personName = this.authService.getName();
    this.getRegisterCompanyInfo();
    this.loadCompanySlots(this.registerCompanyId);

    this.form = new FormGroup({
      participants: new FormArray([], Validators.required),
      slots: new FormArray([], Validators.required),
      presentationFile: new FormControl(null),
    });

    this.subscription.add(this.eventService.eventSubject.subscribe(x => {
      this.eventInfo = x;
      if (this.eventInfo) {
        this.startDate = this.eventInfo.startDateRegisterCompany;
        this.endDate = this.eventInfo.endDateRegisterCompany;
        this.loginLayout.setBackgroundUrl(this.eventInfo.bgUrl); 
      }
    }));

    this.subscription.add(this.dietaryService.dietaries.subscribe(x => {
      this.dietaries = x;
    }));

    this.subscription.add(this.dietaryService.noneDietaryId.subscribe(x => {
      this.noneDietaryId = x;
    }));

    this.subscription.add(this.participantControllerService.getParticipantUsingGET1(this.registerCompanyId).subscribe(res => {
      const participantsArray = this.form.get('participants') as FormArray;
      participantsArray.clear();
      res.data.forEach(p => { participantsArray.push(buildParticipantForm(p, this.registerCompanyId, this.noneDietaryId)); });
      this.lastSavedParticipants = JSON.parse(JSON.stringify(participantsArray.value));
    }));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadCompanySlots(registerCompanyId: number) {
    return this.companySlotControllerService.getCompanySlotUsingGET1(registerCompanyId)
      .subscribe((response: APIResultListManageCompanySlotResponse) => {
        this.slots = response.data || [];
        this.slots.sort((a, b) => new Date(a.slotDate).getTime() - new Date(b.slotDate).getTime());
        this.slots.forEach(slot => {
          slot.slotPeriods.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
        });
        this.form.setControl('slots', this.createSlotsFormArray(this.slots));
        this.lastSavedSlots = JSON.parse(JSON.stringify((this.form.get('slots') as FormArray).value));
      });
  }

  createSlotsFormArray(slots: any[] = []) {
    return new FormArray(slots.map(slot => new FormGroup({
      slotDate: new FormControl(slot.slotDate),
      slotId: new FormControl(slot.slotId),
      slotPeriods: this.createSlotPeriodsFormArray(slot.slotPeriods)
    })), this.validateAvailableTime());
  }

  createSlotPeriodsFormArray(slotPeriods: any[] = []) {
    return new FormArray(slotPeriods.map(sp => {
      const isAvailable = typeof sp.isAvailable === 'boolean' ? sp.isAvailable : sp.available === 'Y';
      return new FormGroup({
        companySlotId: new FormControl(sp.companySlotId),
        description: new FormControl(sp.description),
        endTime: new FormControl(sp.endTime),
        slotPeriodId: new FormControl(sp.slotPeriodId),
        startTime: new FormControl(sp.startTime),
        isAvailable: new FormControl(isAvailable, Validators.required)
      });
    }));
  }

  validateAvailableTime(): ValidatorFn {
    const validator: ValidatorFn = (formArrays: FormArray) => {
      const arrays = formArrays.value.map(m => m.slotPeriods);
      const flattenArrays = [].concat.apply([], arrays)
      const result = flattenArrays.map((m: any) => m.isAvailable).filter((f: boolean) => f).length > 0;

      return result ? null : { selectAtLeaseOneAvailableTime: true };
    }
    return validator;
  }

  private participantsHasChanged(): boolean {
    const currentValue = (this.form.get('participants') as FormArray).value;
    return JSON.stringify(currentValue) !== JSON.stringify(this.lastSavedParticipants);
  }

  private resetParticipantsToLastSaved(): void {
    const participantsArray = this.form.get('participants') as FormArray;
    participantsArray.clear();
    this.lastSavedParticipants.forEach((p: ParticipantRequest) => {
      participantsArray.push(buildParticipantForm(p, this.registerCompanyId, this.noneDietaryId));
    });
  }

  private slotsHasChanged(): boolean {
    const currentValue = (this.form.get('slots') as FormArray).value;
    return JSON.stringify(currentValue) !== JSON.stringify(this.lastSavedSlots);
  }

  private resetSlotsToLastSaved(): void {
    this.form.setControl('slots', this.createSlotsFormArray(this.lastSavedSlots));
  }

  private presentationHasChanged(): boolean {
    const currentFile = this.form.get('presentationFile').value;
    return !!currentFile || this.removeExistingPresentation;
  }

  onPresentationFileChange(file: File): void {
    this.removeExistingPresentation = !!(file == null)
    this.form.get('presentationFile').setValue(file);
  }
  
  goTab(tab: 'participants' | 'availableTime' | 'presentation' | 'meetingSchedule') {
    if (this.activeTab === tab) {
      return;
    }
    if (this.activeTab === 'participants' && this.participantsHasChanged()) {
      this.discardParticipantModal(tab);
      return;
    }
    if (this.activeTab === 'availableTime' && this.slotsHasChanged()) {
      this.discardSlotsModal(tab);
      return;
    }
    if (this.activeTab === 'presentation' && this.presentationHasChanged()) {
      this.discardPresentationModal(tab);
      return;
    }
    this.activeTab = tab;
  }

  discardParticipantModal(tab: 'participants' | 'availableTime' | 'presentation' | 'meetingSchedule'): void {
    this.alert.showDiscardChanges().then((result) => {
      if (result.isConfirmed) {
        this.resetParticipantsToLastSaved();
        this.activeTab = tab;
      }
    });
  }

  discardSlotsModal(tab: 'participants' | 'availableTime' | 'presentation' | 'meetingSchedule'): void {
    this.alert.showDiscardChanges().then((result) => {
      if (result.isConfirmed) {
        this.resetSlotsToLastSaved();
        this.activeTab = tab;
      }
    });
  }

  discardPresentationModal(tab: 'participants' | 'availableTime' | 'presentation' | 'meetingSchedule'): void {
    this.alert.showDiscardChanges().then((result) => {
      if (result.isConfirmed) {
        this.form.get('presentationFile').setValue(null);
        this.removeExistingPresentation = false;
        this.activeTab = tab;
      }
    });
  }

  onSaveParticipants(participants: ParticipantRequest[]): void {
    this.participantControllerService.upsertParticipantsUsingPOST(this.registerCompanyId, participants).subscribe(() => {
      this.lastSavedParticipants = JSON.parse(JSON.stringify(participants));
      this.alert.showSuccess(msg.success.dataSaved);
    });
  }

  onSaveSlots(slots: CompanySlotUpdateRequest): void {
    this.companySlotControllerService.createAndUpdateCompanySlotUsingPOST1(slots, this.registerCompanyId).subscribe(() => {
      this.lastSavedSlots = JSON.parse(JSON.stringify((this.form.get('slots') as FormArray).value));
      this.alert.showSuccess(msg.success.dataSaved);
    });
  }

  onSavePresentation(file: File): void {
    const uploadFile = file || (this.form.get('presentationFile').value as File);

    const hasExistingPresentation = !!(
      this.registerCompanyInfo
      && (this.registerCompanyInfo.presentationUrl || this.registerCompanyInfo.originalPresentationFileName)
    );

    const isDeleteOnly = !uploadFile && hasExistingPresentation;

    const request$ = uploadFile
      ? (hasExistingPresentation
          ? this.registerCompanyService.deleteCompanyPresentationUsingDELETE(this.registerCompanyInfo.registerCompanyId)
              .pipe(
                catchError(() => of(null)),
                switchMap(() => this.registerCompanyService.uploadPresentationUsingPOST(uploadFile, this.registerCompanyInfo.registerCompanyId))
              )
          : this.registerCompanyService.uploadPresentationUsingPOST(uploadFile, this.registerCompanyInfo.registerCompanyId))
      : this.registerCompanyService.deleteCompanyPresentationUsingDELETE(this.registerCompanyInfo.registerCompanyId);

    request$.subscribe({
        next: () => {
          this.form.get('presentationFile').setValue(null, { emitEvent: false });
          this.removeExistingPresentation = false;
          this.getRegisterCompanyInfo();
          this.alert.showSuccess(isDeleteOnly ? msg.success.deleteFile : msg.success.uploadFile);
        },
        error: () => { this.alert.showError(isDeleteOnly ? msg.error.deleteFile : msg.error.uploadFile); }
      });
  }

  onUpdateTimeTable() {
    this.timetableComponent.getCompanySlot(this.registerCompanyId);
  }
  onUpdateInfo() {
    this.infoComponent.getParticipant();
  }
  setCanLeave(event:boolean){
    this.leave = event;
  }

}
